/**
 * Electrical Installation Certificate (EIC) - Validation Engine
 * Uses BS 7671 calculation engine for Zs verification
 */
import ZsCalculator from '../../utils/electrical/bs7671Tables.js';

const parseDevice = (type, rating, disconnectionTime = '0.4s', isRCD = false) => {
  const typeParts = type.split(' ');
  const base = typeParts[0]; // MCB, RCBO, BS
  const variant = typeParts.slice(1).join(' '); // 'Type B', '88-2', etc.

  return {
    type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
    typeVariant: base === 'BS' ? `BS ${variant}` : variant,
    rating: parseInt(rating, 10) || 0,
    disconnectionTime,
    is30mARCD: isRCD
  };
};

export default {
  validate: (data) => {
    const errors = {};
    const warnings = {};

    // Contractor details
    if (!data.contractor?.companyName?.trim()) {
      errors.contractorCompanyName = 'Contractor company name is required';
    }
    if (!data.contractor?.engineerName?.trim()) {
      errors.engineerName = 'Engineer name is required';
    }
    if (!data.contractor?.engineerCardNumber?.trim()) {
      errors.engineerCardNumber = 'Engineer card number is required';
    }

    // Client details
    if (!data.client?.clientName?.trim()) {
      errors.clientName = 'Client name is required';
    }

    // Installation details
    if (!data.installation?.installationAddress?.trim()) {
      errors.installationAddress = 'Installation address is required';
    }
    if (!data.installation?.installationType) {
      errors.installationType = 'Installation type is required';
    }

    // Circuits validation with BS 7671 Zs check
    if (!data.circuits || data.circuits.length === 0) {
      errors.circuits = 'At least one circuit must be recorded';
    } else {
      data.circuits.forEach((circuit, index) => {
        const prefix = `circuit_${index}`;

        if (!circuit.location?.trim()) {
          errors[`${prefix}_location`] = 'Circuit location is required';
        }
        if (!circuit.phase) {
          errors[`${prefix}_phase`] = 'Phase allocation is required';
        }
        if (!circuit.protectiveDevice) {
          errors[`${prefix}_protectiveDevice`] = 'Protective device is required';
        }

        // If device + rating selected, lookup max Zs and compare
        if (circuit.protectiveDevice && circuit.deviceRating) {
          const device = parseDevice(circuit.protectiveDevice, circuit.deviceRating);
          const maxZs = ZsCalculator.getMaxZs(device);

          if (maxZs !== null && circuit.maxZs !== undefined) {
            // Auto-fill the max Zs if not provided
            if (circuit.maxZs === '' || circuit.maxZs === null) {
              circuit.maxZs = maxZs;
            }

            // Check measured Zs against max
            if (circuit.measuredZs && parseFloat(circuit.measuredZs) > maxZs) {
              warnings[`${prefix}_measuredZs`] =
                `Measured Zs (${circuit.measuredZs} Ω) exceeds maximum permitted ` +
                `(${maxZs.toFixed(2)} Ω) for ${circuit.protectiveDevice} ${circuit.deviceRating}A.`;
            }
          }
        }

        // Insulation resistance minimum (BS 7671 requires >= 1.0 MΩ)
        ['insulationResistanceLL', 'insulationResistanceLE', 'insulationResistanceNE'].forEach(irField => {
          const ir = circuit[irField];
          if (ir !== undefined && ir !== null && ir !== '' && parseFloat(ir) < 1.0) {
            warnings[`${prefix}_${irField}`] =
              `Insulation resistance (${ir} MΩ) is below the minimum of 1.0 MΩ.`;
          }
        });

        // RCD trip time verification
        if (circuit.rcdType && circuit.rcdType !== 'None' && circuit.rcdTripTime1x) {
          const tripTime = parseFloat(circuit.rcdTripTime1x);
          if (tripTime > 300) {
            warnings[`${prefix}_rcdTripTime1x`] =
              `RCD trip time at 1Δn (${tripTime} ms) exceeds the recommended 300 ms.`;
          }
        }
      });
    }

    // Declaration
    if (!data.declaration?.certificateNumber?.trim()) {
      errors.certificateNumber = 'Certificate number is required';
    }
    if (!data.declaration?.issueDate) {
      errors.issueDate = 'Issue date is required';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings
    };
  }
};